import React from "react";
import { GoldDivider } from "@/components/luxury";

export default function WhatASixGenerationPedigreeProves() {
  return (
    <>
      <p className="font-spectral italic text-xl md:text-2xl tracking-tight text-[#3a3a3a] leading-snug max-w-2xl">
        Pedigree depth isn't bragging. It's the only reliable way to
        surface inbreeding risk before a litter is on the ground.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <p>
        Three-generation pedigrees are the lingua franca of the dog
        world for one reason: they're easy to fit on a piece of paper.
        That's it. There's nothing biologically special about three
        generations. It's a typesetting artifact from the 1950s that
        survived into the database era because nobody questioned it.
      </p>
      <p>
        Six generations is where the math starts telling the truth.
      </p>

      <h2>What the math actually does</h2>
      <p>
        The Coefficient of Inbreeding (COI) is a percentage that
        estimates how much of a dog's DNA is identical-by-descent from
        a shared ancestor. A COI of 0% means no detected common
        ancestor in the pedigree window. A COI of 25% means the parents
        were full siblings. Anything above ~10% is the territory where
        veterinary genetics literature flags significantly elevated
        risk for the recessive disorders that affect the breed.
      </p>
      <p>
        The catch: COI is computed{" "}
        <em>over the pedigree window you give it.</em> A 3-generation
        COI can't see a great-great-grandparent who appears on both
        sides of the tree, so it returns a falsely low number. The
        deeper the window, the harder it is to hide a common ancestor
        without it showing up in the calculation.
      </p>

      <h2>Why six</h2>
      <p>
        Six generations covers 64 ancestor positions per parent — 126
        unique slots once you remove the dog itself. That's deep enough
        to surface the most common high-risk pattern in modern
        purebreds: a popular sire from twenty years ago whose
        descendants now make up half the breeding population, appearing
        five or six generations back on both sides of a contemporary
        litter.
      </p>
      <p>
        A 3-gen COI on the same litter would have read 0%. The 6-gen
        COI reads 8.4%. One of those numbers is true.
      </p>

      <h2>What we do with it</h2>
      <p>
        Every dog registered on Armstrong K9 has its pedigree stored to
        whatever depth the kennel can fill in. When a kennel goes to
        register a planned litter — by entering the sire and dam — the
        platform computes the COI of the resulting puppies over the
        deepest window we have for both parents and surfaces a warning
        if it crosses the 6.25% line (the equivalent of a single
        first-cousin-once-removed pairing). We don't block the
        registration. We just put the number in front of the kennel
        with a date, a methodology, and a one-click way to drill into
        which ancestor is the bottleneck.
      </p>
      <p>
        Most kennels we've talked to, given the warning, change the
        sire or dam. The math is more persuasive than any opinion.
      </p>

      <h2>What pedigree depth is not</h2>
      <p>
        Six generations is not "more proves more." A pedigree filled
        with placeholder names and unverified ancestors three rings
        deep is worse than a clean three-generation tree, because the
        COI computed over the unverified data will be wrong with high
        confidence. A pedigree is only as good as the audit trail
        behind every name in it.
      </p>
      <p>
        That's why our pedigree submissions go through admin review
        when an unknown ancestor is added — the same{" "}
        <a href="/admin/pedigree-additions" className="text-ink underline underline-offset-4 hover:text-crimson">
          approvals queue
        </a>{" "}
        that handles transfers and breed catalog edits. A name doesn't
        get to count toward the COI calculation until a human has
        looked at it.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <h2>What buyers should ask for</h2>
      <p>
        Three things, in order:
      </p>
      <ol>
        <li>
          The 6-generation pedigree of both parents — not the puppy's
          3-generation summary.
        </li>
        <li>
          The 6-gen COI for the litter, with the date it was computed
          and the registry it was computed against.
        </li>
        <li>
          The breed-club genetic-screening panel results for both
          parents (Embark, Wisdom Panel, or breed-specific). A clean
          COI doesn't help if the parents both carry the same
          recessive disorder mutation.
        </li>
      </ol>
      <p>
        A serious kennel will hand all three over without flinching.
        Anyone who can't, or won't, is selling you something other than
        an audited pedigree.
      </p>
    </>
  );
}
